import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { VitalProperty } from "@/Patient/Models/GrowthCharts/BaseChartModel";
import { CurvesName, GrowthChartsInterpolation } from "@/Patient/Models/GrowthCharts/LoadGrowthCurvesCacheResponse";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";

class PercentileCalculator {
    private _chartInterpolations: { [key: string]: GrowthChartsInterpolation[] };
    private _zScores: { [key: number]: { [key: number]: number } };

    public constructor(chartInterpolation: { [key: string]: GrowthChartsInterpolation[] }, zScores: { [key: number]: { [key: number]: number } }) {
        this._chartInterpolations = chartInterpolation;
        this._zScores = zScores;
    }

    public getPercentile(
        dateOfBirth: Date,
        vitalSign: MedicalItemVitalSign,
        vitalProperty: VitalProperty,
        curveName: CurvesName,
        parseValuesToGrams: boolean,
        bornPrematureWeeks: number
    ): string {
        if (!this._chartInterpolations)
            return "";

        const zScoreVariables = ZScoreVariableMapper.map(this._chartInterpolations[curveName], dateOfBirth, vitalSign, vitalProperty, curveName, bornPrematureWeeks);
        if (!zScoreVariables)
            return "";

        const zScore = ZScoreCalculator.calculate(zScoreVariables, parseValuesToGrams);
        return this.getPercentileFromZScore(zScore);
    }

    private getPercentileFromZScore(zScore: number): string {
        if (isNaN(zScore))
            return null;

        const maxPercentileText = ">99.9";
        const minPercentileText = "<0.1";
        const maxPercentileAllowed = 99.9;
        const minPercentileAllowed = 0;
        const zTableValues = Object.keys(this._zScores).map(score => parseFloat(score));
        const zTableMaxValue = zTableValues.max();
        const zTableMinValue = zTableValues.min();
        const roundedZScore = Math.round(zScore * 100) / 100;
        const oneDecimal = Math.trunc(roundedZScore * 10) / 10;
        if (oneDecimal > zTableMaxValue)
            return maxPercentileText;

        if (oneDecimal < zTableMinValue)
            return minPercentileText;

        const zTable = this._zScores[this.negativeZeroToString(oneDecimal)];
        if (!zTable)
            return null;

        const remainingLastDecimal = Math.abs(Math.round((roundedZScore - oneDecimal) * 100) / 100);
        const percentile = zTable[remainingLastDecimal] * 100;
        if (Math.round(percentile * 10) / 10 > maxPercentileAllowed)
            return maxPercentileText;

        if (Math.round(percentile * 10) / 10 <= minPercentileAllowed)
            return minPercentileText;

        return percentile.toFixed(1);
    }

    // Hack to convert -0 to string. It is required since (-0).toString() returns '0'.
    private negativeZeroToString(value: number): string {
        if (value === 0 && 1 / value < 0)
            return "-0";

        return value.toString();
    }
}

interface ZScoreVariable {
    y?: number;
    L?: number;
    M?: number;
    S?: number;
}

class ZScoreVariableMapper {
    public static map(
        interpolations: GrowthChartsInterpolation[],
        dateOfBirth: Date,
        vitalSign: MedicalItemVitalSign,
        vitalProperty: VitalProperty,
        curveName: CurvesName,
        bornPrematureWeeks: number = 0
    ): ZScoreVariable {
        if (isNaN(vitalSign[vitalProperty]) || !interpolations?.any())
            return null;

        const interpolationValues = InterpolationAndVariableCalculator.calculate(dateOfBirth, vitalSign, curveName, interpolations, bornPrematureWeeks);
        if (!interpolationValues?.interpolation)
            return null;

        const { interpolation, variable } = interpolationValues;
        const l = interpolation.L;
        const m = interpolation.M;
        const s = interpolation.S;

        return {
            y: this.getFieldValue(vitalSign, vitalProperty),
            L: this.evaluatePolynome(variable, l.X0, l.X1 ?? 0, l.X2 ?? 0, l.X3 ?? 0),
            M: this.evaluatePolynome(variable, m.X0, m.X1 ?? 0, m.X2 ?? 0, m.X3 ?? 0),
            S: this.evaluatePolynome(variable, s.X0, s.X1 ?? 0, s.X2 ?? 0, s.X3 ?? 0)
        };
    }

    private static evaluatePolynome(
        coefficient: number,
        a0: number,
        a1: number,
        a2: number,
        a3: number
    ): number {
        return (a3 * (coefficient ** 3)) + (a2 * (coefficient ** 2)) + (a1 * coefficient) + a0;
    }

    private static getFieldValue(vitalSign: MedicalItemVitalSign, vitalProperty: VitalProperty): number {
        const vitalSignObject = new MedicalItemVitalSign(vitalSign);

        switch (vitalProperty) {
            case "Weight":
                return vitalSignObject.getWeightInKilograms();
            case "Height":
                return vitalSignObject.getHeightInCentimeters();
            case "HeadCircumference":
                return vitalSignObject.getHeadCircumferenceInCentimeters();
            default:
                return vitalSignObject[vitalProperty];
        }
    }
}

class ZScoreCalculator {
    public static calculate(variable: ZScoreVariable, parseValuesToGrams: boolean): number {
        if (!variable)
            return null;

        const converter = parseValuesToGrams ? 1000 : 1;
        return (((variable.y * converter / variable.M) ** variable.L) - 1) / (variable.L * variable.S);
    }
}

interface IntervalAndCoefficient {
    interpolation?: GrowthChartsInterpolation;
    variable?: number;
}

class InterpolationAndVariableCalculator {
    private static readonly weightForLengthCharts = ["weightForLenghtMale0-2", "weightForLenghtFemale0-2"];
    private static readonly fentonWeightForLengthCharts = ["fentonLenghtMale", "fentonLenghtFemale", "fentonWeightMale", "fentonWeightFemale", "fentonHeadCircMale", "fentonHeadCircFemale"];

    public static calculate(dateOfBirth: Date, vitalSign: MedicalItemVitalSign, curveName: CurvesName, interpolations: GrowthChartsInterpolation[], bornPrematureWeeks: number = 0): IntervalAndCoefficient {
        if (this.weightForLengthCharts.contains(curveName))
            return this.getInterpolationAndVariableFromLength(new MedicalItemVitalSign(vitalSign).getHeightInCentimeters(), interpolations);
        else if (this.fentonWeightForLengthCharts.contains(curveName))
            return this.getInterpolationAndVariableFromDates(dateOfBirth, vitalSign.PharmaStartDate, interpolations, true, bornPrematureWeeks);
        else
            return this.getInterpolationAndVariableFromDates(dateOfBirth, vitalSign.PharmaStartDate, interpolations);
    }

    private static getInterpolationAndVariableFromDates(dateOfBirth: Date, vitalSignDate: Date, interpolations: GrowthChartsInterpolation[], isDataForWeeks: boolean = false, bornPrematureWeeks: number = 0): IntervalAndCoefficient {
        if (!dateOfBirth)
            return null;

        let variable = 0;
        let interpolation: GrowthChartsInterpolation;

        if (isDataForWeeks) {
            const weeks = LuxonDateHelper.dateDiff(dateOfBirth, vitalSignDate, "weeks");
            const interval = Math.abs(weeks) + bornPrematureWeeks;
            if (this.isValueOutOfBounds(interpolations, interval))
                return null;

            interpolation = interpolations.firstOrDefault(p => p.Interval.toFixed(2) === interval.toFixed(2));
        }
        else {
            const totalMonths = LuxonDateHelper.dateDiff(dateOfBirth, vitalSignDate, "months");
            if (this.isValueOutOfBounds(interpolations, totalMonths))
                return null;

            interpolation = interpolations.firstOrDefault(p => p.Interval >= totalMonths);
            variable = totalMonths - interpolation.LowerBound;
        }

        return {
            interpolation: interpolation,
            variable: variable
        };
    }

    private static getInterpolationAndVariableFromLength(length: number, interpolations: GrowthChartsInterpolation[]): IntervalAndCoefficient {
        if (this.isValueOutOfBounds(interpolations, length))
            return null;

        const interpolation = interpolations.firstOrDefault(p => p.Interval >= length);

        return {
            interpolation: interpolation,
            variable: length - interpolation.LowerBound
        };
    }

    private static isValueOutOfBounds(interpolations: GrowthChartsInterpolation[], value: number): boolean {
        const minInterval = interpolations.map(e => e.LowerBound).min();
        const maxInterval = interpolations.map(e => e.Interval).max();
        if (value < minInterval || value > maxInterval)
            return true;

        return false;
    }
}

export { PercentileCalculator };
